import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Tag, 
  Modal, 
  Form, 
  Select, 
  Input, 
  DatePicker, 
  Switch,
  message,
  Card,
  Row,
  Col,
  Statistic,
  Alert
} from 'antd';
import { 
  PlusOutlined, 
  ReloadOutlined,
  SafetyCertificateOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import request from '../../../utils/request';

const { Option } = Select;
const { TextArea } = Input;

interface SpecialPermission {
  id: number;
  user: number;
  user_username: string;
  user_email: string;
  permission_type: string;
  permission_type_display: string;
  is_granted: boolean;
  granted_by: number;
  granted_by_username: string;
  granted_at: string;
  expires_at?: string;
  reason: string;
}

const SpecialPermissions: React.FC = () => {
  const [permissions, setPermissions] = useState<SpecialPermission[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [userOptions, setUserOptions] = useState<{ label: string; value: number }[]>([]);
  const [fetchingUsers, setFetchingUsers] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [usersPage, setUsersPage] = useState(1);
  const [usersHasMore, setUsersHasMore] = useState(false);

  const PERMISSION_TYPE_CHOICES = [
    { value: 'sensitive', label: '敏感信息' },
    { value: 'financial', label: '财务信息' },
    { value: 'statistics', label: '统计信息' },
  ];

  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const response = await request.get('/api/v1/permission-management/special/');
      setPermissions(response.data.results || response.data);
    } catch (error) {
      message.error('获取特殊权限记录失败');
      console.error('获取特殊权限记录失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    form.resetFields();
    setModalVisible(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      await request.post('/api/v1/permission-management/special/', values);
      message.success('特殊权限设置成功');
      setModalVisible(false);
      fetchPermissions();
    } catch (error) {
      message.error('特殊权限设置失败');
      console.error('特殊权限设置失败:', error);
    }
  };

  const searchUsers = async (keyword: string = '', page: number = 1, append: boolean = false) => {
    setFetchingUsers(true);
    try {
      const res = await request.get('/api/v1/permission-management/users/', {
        params: { search: keyword, page, page_size: 10 },
      });
      const list = res.data?.results || res.data || [];
      const next = res.data?.next;
      const opts = list.map((u: any) => ({ label: `${u.username} (${u.email || '无邮箱'})`, value: u.id }));
      setUserOptions((prev) => (append ? [...prev, ...opts] : opts));
      setUsersHasMore(Boolean(next) || (Array.isArray(list) && list.length === 10));
      setUsersPage(page);
    } catch (e) {
      // ignore
    } finally {
      setFetchingUsers(false);
    }
  };

  const handleUserPopupScroll = (e: any) => {
    const target = e?.target as HTMLElement;
    if (!target) return;
    const nearBottom = target.scrollTop + target.offsetHeight >= target.scrollHeight - 24;
    if (nearBottom && usersHasMore && !fetchingUsers) {
      const nextPage = usersPage + 1;
      searchUsers(userSearch, nextPage, true);
    }
  };

  const columns = [
    {
      title: '用户',
      key: 'user',
      render: (_, record: SpecialPermission) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.user_username}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.user_email}</div>
        </div>
      ),
    },
    {
      title: '权限类型',
      dataIndex: 'permission_type_display',
      key: 'permission_type_display',
      render: (text: string) => <Tag color="purple">{text}</Tag>,
    },
    {
      title: '是否授予',
      dataIndex: 'is_granted',
      key: 'is_granted',
      render: (isGranted: boolean) => (
        <Tag color={isGranted ? 'green' : 'red'}>
          {isGranted ? '已授予' : '未授予'}
        </Tag>
      ),
    },
    {
      title: '授予者',
      dataIndex: 'granted_by_username',
      key: 'granted_by_username',
    },
    {
      title: '授予时间',
      dataIndex: 'granted_at',
      key: 'granted_at',
      render: (date: string) => new Date(date).toLocaleString(),
    },
    {
      title: '过期时间',
      dataIndex: 'expires_at',
      key: 'expires_at',
      render: (date: string) => date ? new Date(date).toLocaleString() : '永不过期',
    },
    {
      title: '原因',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
    },
  ];

  // 统计数据
  const totalPermissions = permissions.length;
  const grantedPermissions = permissions.filter(p => p.is_granted).length;
  const sensitivePermissions = permissions.filter(p => p.permission_type === 'sensitive' && p.is_granted).length;
  const financialPermissions = permissions.filter(p => p.permission_type === 'financial' && p.is_granted).length;
  const statisticsPermissions = permissions.filter(p => p.permission_type === 'statistics' && p.is_granted).length;

  return (
    <div className="special-permissions">
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="总权限记录"
              value={totalPermissions}
              prefix={<SafetyCertificateOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="已授予权限"
              value={grantedPermissions}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="敏感信息权限"
              value={sensitivePermissions}
              prefix={<SafetyCertificateOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="财务信息权限"
              value={financialPermissions}
              prefix={<SafetyCertificateOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="特殊权限 - 使用说明"
          description={
            <div>
              <div>为个别用户临时开关“敏感信息/财务信息/统计信息”等能力。</div>
              <div>不影响其基础角色权限，常用于应急或短期需求，可设置过期时间。</div>
            </div>
          }
        />
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              新增特殊权限
            </Button>
            <Button 
              icon={<ReloadOutlined />}
              onClick={fetchPermissions}
              loading={loading}
            >
              刷新
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={permissions}
          loading={loading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
        />
      </Card>

      <Modal
        title="新增特殊权限"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="user"
            label="用户"
            rules={[{ required: true, message: '请选择用户' }]}
          >
            <Select
              showSearch
              placeholder="搜索用户名/邮箱"
              filterOption={false}
              onSearch={(v) => { setUserSearch(v); searchUsers(v, 1, false); }}
              onFocus={() => { setUserSearch(''); searchUsers('', 1, false); }}
              notFoundContent={fetchingUsers ? '加载中...' : '无匹配用户'}
              options={userOptions}
              onPopupScroll={handleUserPopupScroll}
            />
          </Form.Item>

          <Form.Item
            name="permission_type"
            label="权限类型"
            rules={[{ required: true, message: '请选择权限类型' }]}
          >
            <Select placeholder="请选择权限类型">
              {PERMISSION_TYPE_CHOICES.map(type => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="is_granted"
            label="是否授予"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="expires_at"
            label="过期时间"
          >
            <DatePicker 
              showTime 
              placeholder="选择过期时间（可选）"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="reason"
            label="授予原因"
          >
            <TextArea 
              rows={3} 
              placeholder="请输入授予原因"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SpecialPermissions;
